import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Mail,
  Phone,
  MapPin,
  Instagram,
  Linkedin,
  Facebook,
  Youtube,
  Twitter,
} from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { getApiBase } from "@/lib/api";
import { SUPPORT_EMAIL } from "@/lib/config";

const API = getApiBase();

const PHONES = [
  { display: "+91 99709 58370", whatsapp: "919970958370" },
  { display: "+91 86053 74447", whatsapp: "918605374447" },
];

const SOCIAL_LINKS = [
  { label: "Instagram", icon: Instagram, href: "https://instagram.com/" },
  { label: "LinkedIn", icon: Linkedin, href: "https://linkedin.com/" },
  { label: "Facebook", icon: Facebook, href: "https://facebook.com/" },
  {
    label: "YouTube",
    icon: Youtube,
    href: "https://www.youtube.com/channel/UCeci_oReakCGOao33CC4hjQ",
  },
  { label: "X", icon: Twitter, href: "https://x.com/" },
];

const COL = {
  Product: [
    { name: "Features", to: "/features" },
    { name: "Pricing", to: "/pricing" },
    { name: "Parent App", to: "/parent-app" },
    { name: "Daycare Management", to: "/daycare-management" },
    { name: "Book Demo", to: "/book-demo" },
  ],
  Daycare: [
    { name: "Dashboard", to: "/dashboard" },
    { name: "Attendance", to: "/attendance" },
    { name: "Kids", to: "/kids" },
    { name: "Staff", to: "/staff" },
    { name: "Food Menu", to: "/food-menu" },
    { name: "Gallery", to: "/gallery" },
    { name: "Notifications", to: "/notifications" },
    { name: "Payments", to: "/payments" },
    { name: "Schedule", to: "/schedule" },
  ],
  Parent: [
    { name: "Activity Feed", to: "/activity-feed" },
    { name: "Messages", to: "/messages" },
    { name: "Notifications", to: "/parent-notifications" },
    { name: "Holidays", to: "/holidays" },
    { name: "Lost & Found", to: "/lost-and-found" },
    { name: "Reports", to: "/reports" },
    { name: "Find Daycares", to: "/find-daycares" },
  ],
  Company: [
    { name: "About", to: "/about" },
    { name: "Journal", to: "/blog" },
    { name: "Contact", to: "/contact" },
    { name: "FAQ", to: "/faq" },
  ],
};

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Enter a valid email");
    setLoading(true);
    try {
      const res = await fetch(`${API}/public/playbook-leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: "Newsletter subscriber",
          email,
        }),
      });
      if (!res.ok) throw new Error("Request failed");
      toast.success("You're subscribed. Watch your inbox on Sundays.");
      setEmail("");
    } catch (err) {
      toast.error("Subscription failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      data-testid="footer"
      className="relative mt-24 overflow-hidden border-t border-[#46545a] bg-[#334146]"
    >
      <div className="ss-blob z-0 bg-[var(--cn-sage-dark)] w-[360px] h-[360px] -left-24 top-8 opacity-35" />
      <div className="ss-blob z-0 bg-[var(--cn-terra-dark)] w-[320px] h-[320px] -right-20 bottom-8 opacity-30" />
      <div className="container-ss py-20 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 relative">
          {/* Logo + newsletter */}
          <div className="lg:col-span-3">
            <Link to="/" className="flex items-center gap-2.5 mb-6">
              <LogoMark />
              <span className="font-display text-2xl text-[var(--cn-cream)] leading-none tracking-tight">
                Class
                <span className="italic font-medium text-[var(--cn-terra-soft)]">nora</span>
              </span>
            </Link>
            <p className="font-display text-xl text-[var(--cn-cream)] leading-snug max-w-xs">
              The gentle operating system for daycares, preschools and childcare centres.
            </p>

            <form onSubmit={subscribe} className="mt-8">
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[var(--cn-terra-soft)] mb-3">
                A quiet Sunday read
              </div>
              <div className="flex gap-2 p-1.5 bg-[#3d4a4f] rounded-full border border-[#55646a] shadow-[0_10px_24px_rgba(20,28,33,0.25)]">
                <input
                  data-testid="footer-newsletter-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@centre.com"
                  className="flex-1 min-w-0 bg-transparent px-4 py-2 text-sm text-[var(--cn-cream)] outline-none placeholder:text-[var(--cn-cream)]/60"
                />
                <button
                  data-testid="footer-newsletter-submit"
                  disabled={loading}
                  className="!py-2 !px-4 text-sm rounded-full bg-[var(--cn-cream)] text-[#2f3b3f] font-semibold hover:bg-white transition-colors whitespace-nowrap"
                >
                  {loading ? "..." : "Subscribe"}
                </button>
              </div>
            </form>

            <div className="mt-10 flex items-center gap-3">
              {SOCIAL_LINKS.map(({ label, icon: Icon, href }, i) => (
                <a
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  title={label}
                  data-testid={`footer-social-${i}`}
                  className="w-11 h-11 rounded-full bg-[#415057] border border-[#607078] flex items-center justify-center text-[var(--cn-cream)] shadow-[0_8px_18px_rgba(20,28,33,0.22)] hover:bg-[var(--cn-terra-dark)] hover:border-[var(--cn-terra)] hover:text-white transition-all duration-200"
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns: Product | Daycare | Parent | Company */}
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8">
            {Object.entries(COL).map(([title, links]) => (
              <div key={title}>
                <h4 className="font-display text-xs uppercase tracking-[0.2em] text-[var(--cn-terra-soft)] mb-5">
                  {title}
                </h4>
                <ul className="space-y-3">
                  {links.map((l) => (
                    <li key={l.name}>
                      <Link
                        to={l.to}
                        className="text-sm text-[var(--cn-cream)] hover:text-white transition-colors"
                      >
                        {l.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Find us */}
          <div className="lg:col-span-2">
            <h4 className="font-display text-xs uppercase tracking-[0.2em] text-[var(--cn-terra-soft)] mb-5">
              Find us
            </h4>
            <ul className="space-y-3 text-sm text-[var(--cn-cream)]">
              <li className="flex items-start gap-2.5">
                <Mail
                  className="w-4 h-4 mt-0.5 shrink-0 text-[var(--cn-cream)]/65"
                  strokeWidth={1.75}
                  aria-hidden
                />
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="hover:text-white transition-colors break-all"
                >
                  {SUPPORT_EMAIL}
                </a>
              </li>
              {PHONES.map((phone) => (
                <li key={phone.whatsapp} className="flex items-start gap-2.5">
                  <Phone
                    className="w-4 h-4 mt-0.5 shrink-0 text-[var(--cn-cream)]/65"
                    strokeWidth={1.75}
                    aria-hidden
                  />
                  <a
                    href={`https://wa.me/${phone.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whitespace-nowrap hover:text-white transition-colors"
                  >
                    {phone.display}
                  </a>
                </li>
              ))}
              <li className="flex items-start gap-2.5">
                <MapPin
                  className="w-4 h-4 mt-0.5 shrink-0 text-[var(--cn-cream)]/65"
                  strokeWidth={1.75}
                  aria-hidden
                />
                <span>Pune, India</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#55646a] flex flex-col md:flex-row items-center justify-between gap-3 relative">
          <p className="text-sm text-[var(--cn-cream)]">
            &copy; {new Date().getFullYear()} ClassNora &mdash; made gently, with care.
          </p>
          <div className="flex items-center gap-6 text-sm text-[var(--cn-cream)]">
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Terms</a>
            <a href="#" className="hover:text-white">Security</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
